import { useEffect, useMemo, useRef, useState } from 'react';

import {
  COACH_SUB_FUNCTION_OPTIONS,
  isCoachSubFunctionSlug,
} from '@/constants';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
  Typography,
} from '@/ui';

import type { User } from '@/types';
import type { FC } from 'react';

import { useUpdateCoachProfileMutation } from '@/store/api/coachAssignmentApi';

interface EditCoachSubFunctionsProps {
  canEdit?: boolean;
  coach: User;
  refetchCoach: () => Promise<unknown>;
}

const EditCoachSubFunctions: FC<EditCoachSubFunctionsProps> = ({
  canEdit = false,
  coach,
  refetchCoach,
}) => {
  const coachSubFunctions = coach.coachProfile?.subFunctions;
  const initialSubFunctions = useMemo(
    () => (coachSubFunctions ?? []).filter(isCoachSubFunctionSlug),
    [coachSubFunctions],
  );
  const unknownSubFunctions = useMemo(
    () =>
      (coachSubFunctions ?? []).filter(
        (subFunction) => !isCoachSubFunctionSlug(subFunction),
      ),
    [coachSubFunctions],
  );
  const [subFunctions, setSubFunctions] = useState(initialSubFunctions);
  const loadedCoachIdRef = useRef(coach.id);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [updateCoachProfile] = useUpdateCoachProfileMutation();

  useEffect(() => {
    if (loadedCoachIdRef.current !== coach.id) {
      loadedCoachIdRef.current = coach.id;
      setSubFunctions(initialSubFunctions);
      setSaved(false);
    }
  }, [coach.id, initialSubFunctions]);

  const selectedOptions = useMemo(
    () =>
      COACH_SUB_FUNCTION_OPTIONS.filter((option) =>
        subFunctions.includes(option.slug),
      ),
    [subFunctions],
  );

  const onSave = async () => {
    setIsSaving(true);
    setSaved(false);

    try {
      await updateCoachProfile({
        coachProfile: {
          subFunctions: [...unknownSubFunctions, ...subFunctions],
        },
        id: coach.id,
      }).unwrap();
      await refetchCoach();
      setSaved(true);
    } finally {
      setIsSaving(false);
    }
  };

  const renderContent = () => {
    if (canEdit) {
      return (
        <>
          <Autocomplete
            getOptionLabel={(option) => option.label}
            groupBy={(option) => option.group}
            isOptionEqualToValue={(option, value) => option.slug === value.slug}
            options={COACH_SUB_FUNCTION_OPTIONS}
            renderInput={(params) => (
              <TextField
                {...params}
                helperText="Choose all that apply"
                label="Sub-functions"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.slug}>
                {option.label}
              </Box>
            )}
            value={selectedOptions}
            multiple
            onChange={(_, updatedOptions) => {
              setSubFunctions(updatedOptions.map((option) => option.slug));
              setSaved(false);
            }}
          />
          <div className="flex items-center gap-3">
            <Button loading={isSaving} variant="contained" onClick={onSave}>
              Save
            </Button>
            {saved && <Alert severity="success">Saved.</Alert>}
          </div>
        </>
      );
    }

    if (selectedOptions.length) {
      return (
        <div className="flex flex-wrap gap-2">
          {selectedOptions.map((option) => (
            <Chip key={option.slug} label={option.label} variant="outlined" />
          ))}
        </div>
      );
    }

    return (
      <Typography color="text.disabled" variant="body2">
        No sub-functions selected.
      </Typography>
    );
  };

  return (
    <section className="max-w-2xl space-y-4">
      <div className="space-y-2">
        <Typography variant="h5">Sub-functions</Typography>
      </div>

      {renderContent()}
    </section>
  );
};

export default EditCoachSubFunctions;
